import {
  Avatar,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  Stack,
  Step,
  StepIndicator,
  StepSeparator,
  StepStatus,
  Stepper,
  Text,
} from '@chakra-ui/react';
import { PaperAirplaneIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  SubscriptionInfosFragment,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsBySubscriptionIdQuery,
} from '@blockpulse3/graphql/hooks';
import { Comment, ErrorQueryCard, useErrorToast } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

/**
 * SubscriptionDetailsComments.
 * Comment logic section of the subscription side panel.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsComments({ subscriptionId }: Props): JSX.Element {
  const i18nComments = useTranslations('Comments');

  const { user } = useUser();
  const errorToast = useErrorToast();

  const [value, setValue] = useState<string>();
  const { data, loading, error, refetch } = useGetCommentsBySubscriptionIdQuery({
    variables: { subscriptionId },
  });

  const [createComment] = useCreateCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  if (error) {
    return <ErrorQueryCard h="60px" mx="3" />;
  }

  const handleClick = (): void => {
    if (!value) return;

    createComment({
      variables: {
        createCommentInput: {
          content: value,
          subscriptionId,
        },
      },
      onCompleted: () => {
        setValue('');
        refetch();
      },
    });
  };

  const handleDelete = (commentId: string): void => {
    deleteComment({
      variables: {
        commentId,
      },
      onCompleted: () => refetch(),
      onError: () => errorToast({ title: i18nComments('Toast.Error') }),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(e.target.value);
  };

  return (
    <Stack px="4" spacing="3">
      <Text color="gray.600" fontWeight="600">
        {i18nComments('Title')}
      </Text>
      {loading ? (
        <Skeleton h="60px" />
      ) : (
        <>
          {!!data?.comments?.length && (
            <Stepper gap="0" index={-1} orientation="vertical" w="100%">
              {data?.comments.map((comment, index) => {
                const isAuthor = user?.id === comment.user.id;
                return (
                  <Step key={index} style={{ width: '100%' }}>
                    <StepIndicator>
                      <StepStatus incomplete={<Avatar boxSize="8" />} />
                    </StepIndicator>
                    <Comment
                      date={comment.createdAt}
                      isDeletable={isAuthor}
                      title={comment.user.firstName + ' ' + comment.user.lastName}
                      onDelete={(): void => handleDelete(comment.id)}
                    >
                      {comment.content}
                    </Comment>
                    <StepSeparator />
                  </Step>
                );
              })}
            </Stepper>
          )}
          <InputGroup size="md">
            <Input
              placeholder={i18nComments('Input.Placeholder')}
              pr="4.5rem"
              type="text"
              value={value}
              onChange={handleChange}
              onKeyDown={(e): void => {
                if (e.key === 'Enter') {
                  handleClick();
                }
              }}
            />
            <InputRightElement width="2.5rem">
              <IconButton
                aria-label="add-comment"
                icon={<Icon as={PaperAirplaneIcon} boxSize={5} color="gray.500" />}
                size="sm"
                transform="rotate(90deg)"
                variant="ghost"
                onClick={handleClick}
              />
            </InputRightElement>
          </InputGroup>
        </>
      )}
    </Stack>
  );
}

export type SubscriptionDetailsCommentsProps = Props;
